import React, { useMemo } from 'react';
import { useBookingV2 } from 'context/BookingV2Context';
import { useCartState, useCartDispatch } from 'hooks/useCartV2';
import { useLang } from 'hooks/useLang';
import { ImprovedCartActionsEnum } from 'types/improved-booking';
import SelectionCard from 'components/bookingv2/SelectionCard';
import { Extra } from 'types/event';

const AddOns = () => {
  const { event } = useBookingV2();
  const cartState = useCartState();
  const cartDispatch = useCartDispatch();
  const { t, multiLangVars } = useLang();

  // Get available extras from the event
  const availableExtras = event?.extras || [];

  // Filter extras based on selected tickets and timeslots
  const filteredExtras = useMemo(() => {
    if (!availableExtras.length || !cartState.selectedTickets.length) {
      return [];
    }

    const selectedTimeslotIds = cartState.selectedTickets.map(ticket => ticket.timeslot_id);
    const selectedTicketTypeIds = cartState.selectedTickets.map(ticket => ticket.type_id);

    return availableExtras.filter((extra: Extra) => {
      // Check if extra is available for selected timeslots
      const isTimeslotCompatible = extra.timeslot_ids.length === 0 ||
        extra.timeslot_ids.some(id => selectedTimeslotIds.includes(id));

      // Check if extra is available for selected ticket types
      const isTicketTypeCompatible = extra.ticket_type_ids.length === 0 ||
        extra.ticket_type_ids.some(id => selectedTicketTypeIds.includes(id));

      return isTimeslotCompatible && isTicketTypeCompatible && extra.available > 0;
    });
  }, [availableExtras, cartState.selectedTickets]);

  // Group extras by their group
  const groupedExtras = useMemo(() => {
    const groups: Record<string, Extra[]> = {};

    filteredExtras.forEach((extra: Extra) => {
      const groupKey = extra.en_group || extra.ar_group || 'default';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(extra);
    });

    return Object.entries(groups).map(([groupName, extras]) => ({
      groupName,
      extras
    }));
  }, [filteredExtras]);

  // Handle increasing extra quantity
  const handleIncreaseExtra = (e: React.MouseEvent, extra: Extra) => {
    e.stopPropagation();

    const currentExtra = cartState.selectedExtras.find(
      selectedExtra => selectedExtra.extra_id === extra._id
    );
    const currentQuantity = currentExtra?.qty || 0;

    // Check if we can add more
    if (currentQuantity < extra.max && currentQuantity < extra.available) {
      cartDispatch({
        type: ImprovedCartActionsEnum.ADD_EXTRA,
        payload: {
          extra_id: extra._id,
          en_title: extra.en_title,
          ar_title: extra.ar_title,
          en_group: extra.en_group || '',
          ar_group: extra.ar_group || '',
          price: extra.price,
          qty: 1
        }
      });
    }
  };

  // Handle decreasing extra quantity
  const handleDecreaseExtra = (e: React.MouseEvent, extraId: string) => {
    e.stopPropagation();

    cartDispatch({
      type: ImprovedCartActionsEnum.REMOVE_EXTRA,
      payload: { extra_id: extraId }
    });
  };

  // Get current quantity for an extra
  const getCurrentQuantity = (extraId: string): number => {
    const selectedExtra = cartState.selectedExtras.find(
      extra => extra.extra_id === extraId
    );
    return selectedExtra?.qty || 0;
  };

  // Check if increase is disabled
  const isIncreaseDisabled = (extra: Extra): boolean => {
    const currentQuantity = getCurrentQuantity(extra._id);
    return currentQuantity >= extra.max || currentQuantity >= extra.available;
  };

  // Check if decrease is disabled
  const isDecreaseDisabled = (extraId: string): boolean => {
    return getCurrentQuantity(extraId) <= 0;
  };

  if (!filteredExtras.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            {t('no_extras_available')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('no_extras_available_description')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-ons-container flex flex-col gap-y-6">
      <div className="header-section">
        <h2 className="text-lg lg:text-xl font-semibold text-[#fff] mb-2">
          {t('select_extras')}
        </h2>
        <p className="text-sm text-[#fff]">
          {t('extras_description')}
        </p>
      </div>

      {groupedExtras.map(({ groupName, extras }) => (
        <div key={groupName} className="extras-group">
          {groupName !== 'default' && (
            <h3 className="text-md font-medium text-[#fff] mb-3 capitalize">
              {multiLangVars('group') === 'ar_group' ?
                extras[0].ar_group || groupName :
                extras[0].en_group || groupName
              }
            </h3>
          )}

          <div className="extras-list flex flex-col gap-y-2">
            {extras.map((extra, index) => {
              const currentQuantity = getCurrentQuantity(extra._id);
              const isFirst = index === 0;
              const isLast = index === extras.length - 1;

              return (
                <SelectionCard
                  key={extra._id}
                  variant="ticket"
                  title={extra[multiLangVars('title') as 'en_title' | 'ar_title']}
                  price={extra.price}
                  quantity={currentQuantity}
                  isFirst={isFirst}
                  isLast={isLast}
                  onIncrease={(e) => handleIncreaseExtra(e, extra)}
                  onDecrease={(e) => handleDecreaseExtra(e, extra._id)}
                  increaseDisabled={isIncreaseDisabled(extra)}
                  decreaseDisabled={isDecreaseDisabled(extra._id)}
                  statusMessage={
                    extra.is_required && currentQuantity < extra.min
                      ? `${t('required_extra_min_message')} ${extra.min}`
                      : currentQuantity >= extra.max
                        ? t('extra_max_reached')
                        : undefined
                  }
                  statusColor={
                    extra.is_required && currentQuantity < extra.min
                      ? 'red'
                      : currentQuantity >= extra.max
                        ? 'orange'
                        : undefined
                  }
                  onClick={() => {
                    // Optional: Handle extra details view
                  }}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Required extras notice */}
      {filteredExtras.some((extra: Extra) => extra.is_required) && (
        <div className="notice-section bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-x-2">
            <div className="w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">!</span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800 mb-1">
                {t('required_extras_notice_title')}
              </h4>
              <p className="text-xs text-yellow-700">
                {t('required_extras_notice_description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOns;
